import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { AdminBreadcrumbs } from './admin/components/AdminUiPrimitives';
import { AdminPageHeader } from './admin/components/AdminMasterDetail';

interface EnrollmentItem {
  id: number;
  userId: number;
  courseId: number;
  courseName: string;
  enrollmentDate: string;
  completionPercentage: number;
  isCompleted: boolean;
  completionDate?: string | null;
  subscriptionTier: number;
  stripeSubscriptionId?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
}

const getPlanName = (tier: number): string => {
  if (tier >= 999) return 'Full Access';
  if (tier === 4) return 'Gold';
  if (tier === 3) return 'Silver';
  if (tier === 2) return 'Bronze';
  return 'Free';
};

const formatDate = (dateValue?: string | null): string => {
  if (!dateValue) return 'N/A';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getEnrollmentStatus = (item: EnrollmentItem): { label: string; className: string } => {
  if (!item.subscriptionEndDate) {
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  }

  const end = new Date(item.subscriptionEndDate);
  if (Number.isNaN(end.getTime())) {
    return { label: 'Unknown', className: 'bg-gray-100 text-gray-700' };
  }

  if (end.getTime() >= Date.now()) {
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  }

  return { label: 'Expired', className: 'bg-red-100 text-red-700' };
};

const MyEnrollments: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [items, setItems] = useState<EnrollmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl = `${config.apiBaseUrl}/api`;

  useEffect(() => {
    const fetchEnrollments = async () => {
      if (authLoading) return;

      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const response = await axios.get<EnrollmentItem[]>(`${apiBaseUrl}/courses/user/enrollments`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setItems(response.data ?? []);
      } catch (err) {
        console.error('Failed to load enrollments:', err);
        setError('Failed to load your enrollments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, [authLoading, isAuthenticated, getAccessTokenSilently, apiBaseUrl]);

  const sortedItems = useMemo(() => {
    return [...items].sort((first, second) => {
      const firstTime = new Date(first.enrollmentDate).getTime();
      const secondTime = new Date(second.enrollmentDate).getTime();
      return secondTime - firstTime;
    });
  }, [items]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">Loading your enrollments...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-bold text-gray-900">My Enrollments</h1>
        <p className="mt-2 text-gray-600">Sign in to view your enrolled courses and plan details.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Dashboard', to: '/analytics' },
        { label: 'My Enrollments', isCurrent: true },
      ]} />
      <AdminPageHeader
        title="My Enrollments"
        subtitle="Course enrollment and plan details"
        actions={[
          { key: 'dashboard', label: 'Dashboard', variant: 'secondary', onClick: () => navigate('/analytics') },
        ]}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

        {!error && sortedItems.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-600">No enrollments found yet.</p>
          </div>
        )}

        <div className="space-y-3 md:space-y-4">
          {sortedItems.map((item) => {
            const status = getEnrollmentStatus(item);
            return (
              <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 md:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{item.courseName}</h2>
                    <p className="text-sm text-gray-600">Enrolled on {formatDate(item.enrollmentDate)}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                    {status.label}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-gray-500">Plan</p>
                    <p className="font-semibold text-gray-900">{getPlanName(item.subscriptionTier)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-gray-500">Start Date</p>
                    <p className="font-semibold text-gray-900">{formatDate(item.subscriptionStartDate)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-gray-500">End Date</p>
                    <p className="font-semibold text-gray-900">{formatDate(item.subscriptionEndDate)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-gray-500">Progress</p>
                    <p className="font-semibold text-gray-900">{Math.round(item.completionPercentage)}%</p>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default MyEnrollments;
